import {
  Avatar,
  Box,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useContext, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { baseUrl } from "../../services/AzureStorage";
import Helper from "../../common/Helper";
import RouteConstants from "../../common/RouteConstants";
import { useNavigate } from "react-router-dom";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import GroupCombineStatsRadarChart from "./GroupCombineStatsRadarChart";
import { AppDataContext } from "../../common/appContext";
import HistoricalCombineStatsBarChart from "./HistoricalCombineStatsBarChart";

const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
  },
}));

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "5px 15px",
    border: "1px solid lightgrey",
    borderRadius: "10px",
    boxShadow: "2px 2px 11px grey inset",
  },
  listItemIcon: {
    minWidth: "38px",
  },
  imageIcon: {
    width: "100%",
    height: "100%",
    cursor: "pointer",
  },
  iconRoot: {
    textAlign: "center",
    width: "100%",
    height: "50px",
  },
  cardStyle: {
    border: "1px solid lightgrey",
    borderRadius: "10px",
    height: "250px",
  },

  headerText: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  headerText2: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  headerTextInfo: {
    color: "rgb(29, 66, 138)",
    fontSize: "14px",
  },
  headerBox: {
    background: "#c1d0ed",
  },
  smBlue: {
    marginLeft: "16px",
    width: 26,
    height: 26,
    color: theme.palette.getContrastText("#0c2c75"),
    backgroundColor: "#1e3f7e",
    position: "relative",
    overflow: "hidden",
    borderRadius: "50%",
  },
  playerNameStyle: {
    cursor: "pointer",
    "&:hover": {
      color: "blue",
    },
  },
  dataField: {
    backgroundColor: "#1d428a",
    color: "white",
    width: "fit-content",
    padding: "3px",
    fontSize: "0.875rem",
    fontWeight: "400",
  },

  percentileField: {
    width: "fit-content",
    padding: "3px",
    fontSize: "0.875rem",
    fontWeight: "bold",
  },
  cellContent: {
    padding: "5px",
    textAlign: "center",
  },
  percentileCellContent: {
    display: "flex",
    justifyContent: "center",
  },
}));

const HistoricalPercentileStrengthAndAgility = (props) => {
  const classes = useStyles();
  let navigate = useNavigate();
  const {
    strengthAndAgility,
    eventId,
    strengthAndAgilityRadarData,
    loading,
    validateNumber,
    primaryPlayers,
    strengthAndAgilityAVGRadarData,
    strengthAndAgilityGroupPercentile,
    isShowGroupPercentile,
  } = props;

  const {
    getHandDimensionsUnitValue,
    getHandDimensionsUnitLabelType,
    lengthUnits,
  } = useContext(AppDataContext);

  //#Destructuring Data
  const { primaryPlayer, secondaryPlayer, groupPercentile } =
    strengthAndAgility;

  const {
    data: primaryPlayerData,
    avg: avgPrimary,
    percentile: percentilePrimary,
  } = primaryPlayer;
  const {
    data: secondaryPlayerData,
    avg: avgSecondary,
    percentile: percentileSecondary,
  } = secondaryPlayer;

  //#Preaparing Percentile Radar Data
  const labels = strengthAndAgilityRadarData.map((val) => val.category);
  const compareGroup1Data = strengthAndAgilityRadarData.map((val) =>
    parseFloat(val.primaryPlayerPercentile).toFixed(2)
  );
  const compareGroup2Data = strengthAndAgilityRadarData.map((val) =>
    parseFloat(val.secondaryPlayerPercentile).toFixed(2)
  );

  //#Preaparing AVG Radar Data
  const avGLabels = strengthAndAgilityAVGRadarData.map((val) => val.category);
  const aVGCompareGroup1Data = strengthAndAgilityAVGRadarData.map((val) =>
    parseInt(val.primaryPlayerAVG)
  );
  const aVGCompareGroup2Data = strengthAndAgilityAVGRadarData.map((val) =>
    val.groupAVG.toString()
  );

  //#Preaparing Group Percentile Radar Data
  const groupPercentileLabels = strengthAndAgilityGroupPercentile.map(
    (val) => val.category
  );
  const compareGroupPercentile1 = strengthAndAgilityGroupPercentile.map((val) =>
    parseFloat(val.primaryPlayerPercentile).toFixed(2)
  );
  const compareGroupPercentile2 = strengthAndAgilityGroupPercentile.map((val) =>
    parseFloat(val.secondaryPlayerPercentile).toFixed(2)
  );

  const viewPlayerProfile = (playerId) => {
    navigate(`/${eventId}${RouteConstants.VIEW_PLAYER_PROFILE}/${playerId}`, {
      state: { from: "CombineResults", tab: 0 },
    });
  };

  const historicalPercentileSASColumn = [
    {
      id: "Player Name",
      headerName: "Player Name",
      style: { padding: "4px", paddingLeft: "25px" },
      unit: "",
    },
    {
      id: "Position",
      headerName: "Position",
      style: { padding: "4px", textAlign: "center" },
      unit: "",
    },
    {
      id: "College Name",
      headerName: "College Name",
      style: { padding: "4px", textAlign: "center" },
      unit: "",
    },
    {
      id: "Lane Shuttle Left",
      headerName: "Lane Shuttle Left",
      style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
      unit: `(sec)`,
    },
    {
      id: "Lane Shuttle Right",
      headerName: "Lane Shuttle Right",
      style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
      unit: `(sec)`,
    },
    {
      id: "3/4 Court Sprint",
      headerName: "3/4 Court Sprint",
      style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
      unit: `(sec)`,
    },
    {
      id: "Pro Lane",
      headerName: "Pro Lane",
      style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
      unit: `(sec)`,
    },
    {
      id: "Max Vertical Jump",
      headerName: "Max Vertical Jump",
      style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
      unit: `${getHandDimensionsUnitLabelType(lengthUnits)}`,
    },
    {
      id: "Standing Vertical",
      headerName: "Standing Vertical",
      style: { padding: "4px", textAlign: "center", verticalAlign: "top" },
      unit: `${getHandDimensionsUnitLabelType(lengthUnits)}`,
    },
  ];

  const showAvgGroup = () =>
    (primaryPlayerData.length === 1 && secondaryPlayerData.length === 1) ||
    !primaryPlayers;

  return (
    <>
      <Container maxWidth={false}>
        <Box p={1}>
          {loading ? (
            <Box sx={{ textAlign: "center", minHeight: "50px" }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Paper elevation={3} sx={{ my: 2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  className={classes.headerBox}
                >
                  <Box display="flex" pl={2}>
                    <Typography className={`${classes.headerText} headerTitle`}>
                      Group 1
                    </Typography>
                  </Box>
                </Box>
                <Box p={2}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {historicalPercentileSASColumn.map((col, index) => (
                            <BootstrapTooltip
                              title={col.id}
                              placement="top-start"
                              key={index}
                            >
                              <TableCell style={col.style}>
                                {col.headerName}
                              </TableCell>
                            </BootstrapTooltip>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableHead>
                        <TableRow>
                          {historicalPercentileSASColumn.map((col, index) => (
                            <TableCell style={col.style} key={index}>
                              {col.unit}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {primaryPlayerData.length > 0 ? (
                          <>
                            {/* Display Comparable Group 1 Details for Strength And Agility */}
                            {primaryPlayerData?.map((val, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell
                                    className={classes.stickyColumn}
                                    sx={{ padding: "4px" }}
                                  >
                                    <Box display="flex">
                                      {val.headshotURL &&
                                      val.headshotURL !== "" ? (
                                        <Avatar
                                          className={classes.smBlue}
                                          sx={{
                                            bgcolor: "#1d428a",
                                            cursor: "pointer",
                                          }}
                                          src={
                                            val.headshotURL
                                              ? baseUrl(val.headshotURL)
                                              : ""
                                          }
                                          alt="basketball"
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          <Typography variant="caption">
                                            {Helper.GetNameInitials(
                                              `${val.playerName}`
                                            )}
                                          </Typography>
                                        </Avatar>
                                      ) : (
                                        <Avatar
                                          className={classes.smBlue}
                                          sx={{
                                            bgcolor: "#1d428a",
                                            cursor: "pointer",
                                          }}
                                          alt="basketball"
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          <Typography variant="caption">
                                            {Helper.GetNameInitials(
                                              `${val.playerName}`
                                            )}
                                          </Typography>
                                        </Avatar>
                                      )}
                                      &nbsp;&nbsp;
                                      <BootstrapTooltip
                                        title="View Player Profile"
                                        placement="right-start"
                                      >
                                        <Typography
                                          variant="body2"
                                          className={classes.playerNameStyle}
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          {val.playerName}
                                        </Typography>
                                      </BootstrapTooltip>
                                    </Box>
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.position ? val.position : "-"}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.collegeOrClub
                                      ? val.collegeOrClub
                                      : "-"}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.shuttleRunLeftTrialTime
                                      ? validateNumber(
                                          val.shuttleRunLeftTrialTime
                                        )
                                      : 0}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.shuttleRunRightTrialTime
                                      ? validateNumber(
                                          val.shuttleRunRightTrialTime
                                        )
                                      : 0}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.threeFourRunTime
                                      ? validateNumber(val.threeFourRunTime)
                                      : 0}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.proLaneTrialTime
                                      ? validateNumber(val.proLaneTrialTime)
                                      : 0}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {getHandDimensionsUnitValue(
                                      val.maxVerticalLeap
                                        ? validateNumber(val.maxVerticalLeap)
                                        : 0
                                    )}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {getHandDimensionsUnitValue(
                                      val.standingVerticalLeap
                                        ? validateNumber(
                                            val.standingVerticalLeap
                                          )
                                        : 0
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}

                            {/* Display Comparable Group 1 Average Details for Strength And Agility */}
                            {avgPrimary &&
                            Object.keys(avgPrimary).length > 0 ? (
                              <TableRow
                                sx={{
                                  backgroundColor: "#F5F5F5",
                                }}
                              >
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {"AVG"}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgPrimary.shuttleRunLeftTrialTime
                                    ? validateNumber(
                                        avgPrimary.shuttleRunLeftTrialTime
                                      )
                                    : 0}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgPrimary.shuttleRunRightTrialTime
                                    ? validateNumber(
                                        avgPrimary.shuttleRunRightTrialTime
                                      )
                                    : 0}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgPrimary.threeFourRunTime
                                    ? validateNumber(
                                        avgPrimary.threeFourRunTime
                                      )
                                    : 0}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgPrimary.proLaneTrialTime
                                    ? validateNumber(
                                        avgPrimary.proLaneTrialTime
                                      )
                                    : 0}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgPrimary.maxVerticalLeap
                                    ? getHandDimensionsUnitValue(
                                        validateNumber(
                                          avgPrimary.maxVerticalLeap
                                        )
                                      )
                                    : 0}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgPrimary.standingVerticalLeap
                                    ? getHandDimensionsUnitValue(
                                        validateNumber(
                                          avgPrimary.standingVerticalLeap
                                        )
                                      )
                                    : 0}
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}

                            {/* Display Comparable Group 1 Average Details for Strength And Agility */}
                            {percentilePrimary &&
                            Object.keys(percentilePrimary).length > 0 ? (
                              <TableRow
                                sx={{
                                  backgroundColor: "#F5F5F5",
                                }}
                              >
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {"PERCENTILE"}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {percentilePrimary.shuttleRunLeftTrialTime
                                        ? `${percentilePrimary.shuttleRunLeftTrialTime}%`
                                        : "-"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {percentilePrimary.shuttleRunRightTrialTime
                                        ? `${percentilePrimary.shuttleRunRightTrialTime}%`
                                        : "-"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {percentilePrimary.threeFourRunTime
                                        ? `${percentilePrimary.threeFourRunTime}%`
                                        : "-"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {percentilePrimary.proLaneTrialTime
                                        ? `${percentilePrimary.proLaneTrialTime}%`
                                        : "-"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {percentilePrimary.maxVerticalLeap
                                        ? `${percentilePrimary.maxVerticalLeap}%`
                                        : "0%"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {percentilePrimary.standingVerticalLeap
                                        ? `${percentilePrimary.standingVerticalLeap}%`
                                        : "0%"}
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}
                          </>
                        ) : (
                          <TableRow>
                            <TableCell colSpan={10} align="center">
                              No Records Found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>

              <Paper elevation={3} sx={{ my: 2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  className={classes.headerBox}
                >
                  <Box display="flex" pl={2}>
                    <Typography className={`${classes.headerText} headerTitle`}>
                      Group 2
                    </Typography>
                  </Box>
                </Box>
                <Box p={2}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {historicalPercentileSASColumn.map((col, index) => (
                            <BootstrapTooltip
                              title={col.id}
                              placement="top-start"
                              key={index}
                            >
                              <TableCell style={col.style}>
                                {col.headerName}
                              </TableCell>
                            </BootstrapTooltip>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableHead>
                        <TableRow>
                          {historicalPercentileSASColumn.map((col, index) => (
                            <TableCell style={col.style} key={index}>
                              {col.unit}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {secondaryPlayerData.length > 0 ? (
                          <>
                            {/* Display Comparable Group 2 Details for Strength And Agility */}
                            {secondaryPlayerData?.map((val, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell
                                    className={classes.stickyColumn}
                                    sx={{ padding: "5px" }}
                                  >
                                    <Box display="flex">
                                      {val.headshotURL &&
                                      val.headshotURL !== "" ? (
                                        <Avatar
                                          className={classes.smBlue}
                                          sx={{
                                            bgcolor: "#1d428a",
                                            cursor: "pointer",
                                          }}
                                          src={
                                            val.headshotURL
                                              ? baseUrl(val.headshotURL)
                                              : ""
                                          }
                                          alt="basketball"
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          <Typography variant="caption">
                                            {Helper.GetNameInitials(
                                              `${val.playerName}`
                                            )}
                                          </Typography>
                                        </Avatar>
                                      ) : (
                                        <Avatar
                                          className={classes.smBlue}
                                          sx={{
                                            bgcolor: "#1d428a",
                                            cursor: "pointer",
                                          }}
                                          alt="basketball"
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          <Typography variant="caption">
                                            {Helper.GetNameInitials(
                                              `${val.playerName}`
                                            )}
                                          </Typography>
                                        </Avatar>
                                      )}
                                      &nbsp;&nbsp;
                                      <BootstrapTooltip
                                        title="View Player Profile"
                                        placement="right-start"
                                      >
                                        <Typography
                                          variant="body2"
                                          className={classes.playerNameStyle}
                                          onClick={() =>
                                            viewPlayerProfile(val.playerID)
                                          }
                                        >
                                          {val.playerName}
                                        </Typography>
                                      </BootstrapTooltip>
                                    </Box>
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.position ? val.position : "-"}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.collegeOrClub
                                      ? val.collegeOrClub
                                      : "-"}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.shuttleRunLeftTrialTime
                                      ? validateNumber(
                                          val.shuttleRunLeftTrialTime
                                        )
                                      : 0}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.shuttleRunRightTrialTime
                                      ? validateNumber(
                                          val.shuttleRunRightTrialTime
                                        )
                                      : 0}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.threeFourRunTime
                                      ? validateNumber(val.threeFourRunTime)
                                      : 0}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.proLaneTrialTime
                                      ? validateNumber(val.proLaneTrialTime)
                                      : 0}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.maxVerticalLeap
                                      ? getHandDimensionsUnitValue(
                                          validateNumber(val.maxVerticalLeap)
                                        )
                                      : 0}
                                  </TableCell>
                                  <TableCell className={classes.cellContent}>
                                    {val.standingVerticalLeap
                                      ? getHandDimensionsUnitValue(
                                          validateNumber(
                                            val.standingVerticalLeap
                                          )
                                        )
                                      : 0}
                                  </TableCell>
                                </TableRow>
                              );
                            })}

                            {/* Display Comparable Group 2 Average Details for Strength And Agility */}
                            {avgSecondary &&
                            Object.keys(avgSecondary).length > 0 ? (
                              <TableRow
                                sx={{
                                  backgroundColor: "#F5F5F5",
                                }}
                              >
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {"AVG"}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgSecondary.shuttleRunLeftTrialTime
                                    ? validateNumber(
                                        avgSecondary.shuttleRunLeftTrialTime
                                      )
                                    : 0}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgSecondary.shuttleRunRightTrialTime
                                    ? validateNumber(
                                        avgSecondary.shuttleRunRightTrialTime
                                      )
                                    : 0}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgSecondary.threeFourRunTime
                                    ? validateNumber(
                                        avgSecondary.threeFourRunTime
                                      )
                                    : 0}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgSecondary.proLaneTrialTime
                                    ? validateNumber(
                                        avgSecondary.proLaneTrialTime
                                      )
                                    : 0}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgSecondary.maxVerticalLeap
                                    ? getHandDimensionsUnitValue(
                                        validateNumber(
                                          avgSecondary.maxVerticalLeap
                                        )
                                      )
                                    : 0}
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  {avgSecondary.standingVerticalLeap
                                    ? getHandDimensionsUnitValue(
                                        validateNumber(
                                          avgSecondary.standingVerticalLeap
                                        )
                                      )
                                    : 0}
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}

                            {/* Display Comparable Group 2 Average Details for Strength And Agility */}
                            {percentileSecondary &&
                            Object.keys(percentileSecondary).length > 0 ? (
                              <TableRow
                                sx={{
                                  backgroundColor: "#F5F5F5",
                                }}
                              >
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {"PERCENTILE"}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {percentileSecondary.shuttleRunLeftTrialTime
                                        ? `${percentileSecondary.shuttleRunLeftTrialTime}%`
                                        : "-"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {percentileSecondary.shuttleRunRightTrialTime
                                        ? `${percentileSecondary.shuttleRunRightTrialTime}%`
                                        : "-"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {percentileSecondary.threeFourRunTime
                                        ? `${percentileSecondary.threeFourRunTime}%`
                                        : "-"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {percentileSecondary.proLaneTrialTime
                                        ? `${percentileSecondary.proLaneTrialTime}%`
                                        : "-"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {percentileSecondary.maxVerticalLeap
                                        ? `${percentileSecondary.maxVerticalLeap}%`
                                        : "0%"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {percentileSecondary.standingVerticalLeap
                                        ? `${percentileSecondary.standingVerticalLeap}%`
                                        : "0%"}
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}
                          </>
                        ) : (
                          <TableRow>
                            <TableCell colSpan={10} align="center">
                              No Records Found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>

              {/* GROUP PERCENTILE SECTION */}
              {isShowGroupPercentile === "true" ? (
                <>
                  <Paper elevation={3} sx={{ my: 2 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      flexDirection="row"
                      className={classes.headerBox}
                    >
                      <Box display="flex" pl={2}>
                        <Typography
                          className={`${classes.headerText} headerTitle`}
                        >
                          Group Percentile
                        </Typography>
                      </Box>
                    </Box>
                    <Box p={2}>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              {historicalPercentileSASColumn.map(
                                (col, index) => (
                                  <BootstrapTooltip
                                    title={col.id}
                                    placement="top-start"
                                    key={index}
                                  >
                                    <TableCell style={col.style}>
                                      {col.headerName}
                                    </TableCell>
                                  </BootstrapTooltip>
                                )
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* Display Comparable Group 2 Average Details for Strength And Agility */}
                            {groupPercentile &&
                            Object.keys(groupPercentile).length > 0 ? (
                              <TableRow>
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell
                                  className={classes.cellContent}
                                ></TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Typography
                                    variant="subtitle2"
                                    display="block"
                                    gutterBottom
                                  >
                                    {""}
                                  </Typography>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.shuttleRunLeftTrialTime
                                        ? `${groupPercentile.shuttleRunLeftTrialTime}%`
                                        : "-"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.shuttleRunRightTrialTime
                                        ? `${groupPercentile.shuttleRunRightTrialTime}%`
                                        : "-"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.threeFourRunTime
                                        ? `${groupPercentile.threeFourRunTime}%`
                                        : "-"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.proLaneTrialTime
                                        ? `${groupPercentile.proLaneTrialTime}%`
                                        : "-"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.maxVerticalLeap
                                        ? `${groupPercentile.maxVerticalLeap}%`
                                        : "0%"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className={classes.cellContent}>
                                  <Box
                                    className={classes.percentileCellContent}
                                  >
                                    <Box className={classes.dataField}>
                                      {groupPercentile.standingVerticalLeap
                                        ? `${groupPercentile.standingVerticalLeap}%`
                                        : "0%"}
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : (
                              ""
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Paper>
                </>
              ) : null}

              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ my: 2 }}
              >
                {/* <Grid item md={6} sm={12} xs={12} justifyContent="center">
                  {strengthAndAgilityAVGRadarData.length > 0 ? (
                    <GroupCombineStatsRadarChart
                      labels={avGLabels}
                      data={aVGCompareGroup1Data}
                      name={"Group 1"}
                      otherPlayerdata={aVGCompareGroup2Data}
                      nameotherplayer={"Group 2"}
                      chartName={"Historical Group Average "}
                      isPercentileChart={false}
                      max={300}
                    />
                  ) : (
                    ""
                  )}
                </Grid> */}

                <Grid item md={6} sm={12} xs={12} justifyContent="center">
                  {strengthAndAgilityRadarData.length > 0 ? (
                    // <GroupCombineStatsRadarChart
                    //   labels={labels}
                    //   data={compareGroup1Data}
                    //   name={"Group 1"}
                    //   otherPlayerdata={compareGroup2Data}
                    //   nameotherplayer={"Group 2"}
                    //   chartName={"Historical Group Percentile"}
                    //   max={100}
                    // />
                    <HistoricalCombineStatsBarChart
                      labels={labels}
                      priamryPlayerData={compareGroup1Data}
                      primaryPlayerName={"Group 1"}
                      otherPlayerdata={compareGroup2Data}
                      nameOtherPlayer={"Group 2"}
                      chartName={"Historical Group Percentile"}
                      max={100}
                    />
                  ) : (
                    ""
                  )}
                </Grid>
              </Grid>

              {isShowGroupPercentile === "true" ? (
                <>
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ my: 2 }}
                  >
                    <Grid item md={6} sm={12} xs={12} justifyContent="center">
                      {strengthAndAgilityGroupPercentile.length > 0 ? (
                        <HistoricalCombineStatsBarChart
                          labels={groupPercentileLabels}
                          priamryPlayerData={compareGroupPercentile1}
                          primaryPlayerName={"Group 1"}
                          otherPlayerdata={compareGroupPercentile2}
                          nameOtherPlayer={"Group Percentile"}
                          chartName={"Historical Combined Group Percentile"}
                          max={100}
                        />
                      ) : (
                        ""
                      )}
                    </Grid>
                  </Grid>
                </>
              ) : null}
            </>
          )}
        </Box>
      </Container>
    </>
  );
};

export default HistoricalPercentileStrengthAndAgility;
